// Camera-domain clips client.
//
// Backed by two BFF endpoints introduced for the Past Recordings View
// redesign (LLD 13 §11):
//
//   - POST /api/clips:resolve
//       Resolves an arbitrary (device, start_ms, duration_ms, tier) range
//       into an ordered list of <=10-min MP4 segments. This is THE
//       playback primitive for single-view, range, and grid; everything
//       above the wire stitches its segments.
//
//   - POST /api/events/{ding_id}/playback-session
//       Returns the MP4 envelope for an event (segments[] plus the first
//       segment's playback_url for convenience). Segment shape matches
//       /api/clips:resolve so the stitch engine consumes both.
//
// Defects this client documents (LLD 13 §12):
//   1. /api/events/{id}/playback-session must return the MP4 envelope —
//      the legacy /api/playbackClient.ts was wired against the WebRTC
//      ticket shape (live-view) which never plays back recorded clips.
//   2. The backend's MockClipsAccessor ships mock-ring-clips.example.com
//      URLs; pages must tolerate `playback_url=null` (segment can render
//      as a "no footage" gap).
//
// 416 MEDIA_NOT_FOUND from the pre-signed AVA GET inside a segment is a
// recording GAP (camera offline/event-only), not a hard error — the
// stitch engine treats it as "skip to next segment with footage" and
// surfaces the gap inline. See LLD 13 §13.

import { api } from "./client";

/** Segment tier — `"scan"` is SD/360p, `"hd"` is HD/1080p. */
export type ClipTier = "scan" | "hd";

/**
 * One <=10-min stitch segment. Wire shape matches both
 * /api/clips:resolve and /api/events/{id}/playback-session segments
 * so the same engine can play either.
 */
export interface ClipSegment {
  /** 0-based index in the segment list (the order the engine plays them). */
  index: number;
  /** Wall-clock start of this segment (epoch ms). */
  start_ms: number;
  /**
   * Duration the segment covers in the virtual stitched timeline (ms).
   * AVA may return a slightly different actual file duration; advancing
   * the virtual cursor by this value avoids drift (per AVA research §6).
   */
  duration_ms: number;
  /**
   * Pre-signed MP4 URL. May be relative ("/media/foo.mp4") — callers
   * must concatenate the API base via {@link clipsClient.resolveMediaUrl}.
   * `null` means "no footage at this slot" (gap); the engine should
   * advance past it without firing a doomed request.
   */
  playback_url: string | null;
  /** SD/HD tier this segment was rendered at. */
  tier: ClipTier;
  /** Best-effort byte size; informational. */
  bytes?: number | null;
  /**
   * `true` when the segment runs into the live edge (AVA returned 206
   * Partial; footage still finalizing). Treat short returns as success.
   */
  partial?: boolean;
}

/** /api/clips:resolve request body. */
export interface ResolveClipsRequest {
  device_id: string;
  start_ms: number;
  duration_ms: number;
  tier: ClipTier;
}

/** /api/clips:resolve response body. */
export interface ResolveClipsResponse {
  device_id: string;
  start_ms: number;
  duration_ms: number;
  tier: ClipTier;
  /** Backend's hard cap for any one segment (informational; usually 10*60_000). */
  seg_cap_ms: number;
  segment_count: number;
  segments: ClipSegment[];
  /** Pre-signed URL TTL hint (seconds); UI can refresh before this expires. */
  url_ttl_seconds: number;
}

/** /api/events/{id}/playback-session request body. */
export interface CreateRecordedSessionRequest {
  device_id: string;
  /** Optional clamp: defaults to event.duration_seconds * 1000 server-side. */
  duration_ms?: number;
  /** Optional tier; server defaults to `"hd"` (this is event-clip playback). */
  tier?: ClipTier;
}

/**
 * /api/events/{id}/playback-session response — the MP4 envelope.
 *
 * Differs from the legacy WebRTC playback session shape: no signalling
 * ticket / SDP, no teardown URL, just MP4 bytes via a pre-signed URL +
 * a stitch plan in `segments[]`.
 */
export interface RecordedPlaybackSession {
  transport: "mp4";
  event_id: string;
  device_id: string;
  start_ms: number;
  duration_ms: number;
  /** Convenience pointer to segments[0].playback_url. */
  playback_url: string;
  /** ISO-8601 expiry of `playback_url` (and segment URLs share the same TTL). */
  playback_url_expires_at: string;
  segments: ClipSegment[];
}

/**
 * Resolve a server-relative segment URL ("/media/foo.mp4") against the
 * current API origin. AVA pre-signed URLs are absolute and pass through
 * untouched; the local mock server emits relative paths.
 */
function resolveMediaUrl(playbackUrl: string | null): string | null {
  if (playbackUrl === null) return null;
  if (/^https?:\/\//i.test(playbackUrl)) return playbackUrl;
  // Same-origin: a leading "/media/..." resolves naturally against the
  // current page origin (which is also where the BFF proxies to today).
  return playbackUrl;
}

export const clipsClient = {
  /**
   * POST /api/clips:resolve — turn an arbitrary range into ordered MP4 segments.
   *
   * The single playback primitive for single-view, range selection, and
   * the synced 2x2 grid (LLD 13 §11). Server hard-caps `duration_ms` and
   * returns `400 range_too_wide` if exceeded.
   */
  resolve(
    body: ResolveClipsRequest,
    signal?: AbortSignal,
  ): Promise<ResolveClipsResponse> {
    return api.post<ResolveClipsResponse>("/api/clips:resolve", body, { signal });
  },

  /**
   * POST /api/events/{ding_id}/playback-session — MP4 envelope for an event.
   *
   * Replaces the legacy WebRTC-ticket shape from src/api/playbackClient.ts
   * (which never produced playable bytes for recorded clips — LLD 13 §12-1).
   */
  createRecordedSession(
    eventId: string,
    body: CreateRecordedSessionRequest,
    signal?: AbortSignal,
  ): Promise<RecordedPlaybackSession> {
    return api.post<RecordedPlaybackSession>(
      `/api/events/${encodeURIComponent(eventId)}/playback-session`,
      body,
      { signal },
    );
  },

  resolveMediaUrl,
};
